import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export const useHistoricalReview = (initial, jobId) => {
  const [preview, setPreview] = useState(initial), [form, setForm] = useState(initial.effective_decisions);
  const formRef = useRef(form), sequence = useRef(0), request = useRef(null);
  const [dirty, setDirty] = useState(false), [busy, setBusy] = useState(false), [error, setError] = useState("");
  const [editing, setEditing] = useState({});
  useEffect(() => () => { sequence.current += 1; request.current?.abort(); }, []);
  const change = patch => { formRef.current = { ...formRef.current, ...patch }; setForm(formRef.current); setDirty(true); setError(""); sequence.current += 1; };
  const recalculate = async patch => {
    if (patch) change(patch);
    const current = ++sequence.current, decisions = formRef.current;
    request.current?.abort(); const controller = new AbortController(); request.current = controller;
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/reader/jobs/${jobId}/quote-prep/recalculate`, { ...decisions, calculation_fingerprint: null }, { signal: controller.signal });
      if (current === sequence.current) { setPreview(data); formRef.current = data.effective_decisions; setForm(formRef.current); setDirty(false); }
      return data;
    } catch (e) {
      if (e.code !== "ERR_CANCELED" && current === sequence.current) { setError(formatApiErrorDetail(e.response?.data?.detail)); setDirty(true); }
      return null;
    } finally { if (request.current === controller) setBusy(false); }
  };
  const commit = (key, value) => {
    const overrides = { ...(formRef.current.review_overrides || {}) };
    const pairs = [["cut_setup_lot", "cut_setup_share"], ["bend_setup_lot", "bend_setup_share"], ["programming_lot", "programming_share"]];
    if (value != null) pairs.forEach(pair => { if (pair.includes(key)) delete overrides[pair.find(k => k !== key)]; });
    if (key.endsWith("_cava") && value != null) delete overrides[key.replace(/_cava$/, "_v")];
    if (value == null) delete overrides[key]; else overrides[key] = value;
    return recalculate({ review_overrides: overrides });
  };
  const onEditing = useCallback((key, active) => setEditing(old => { const next = { ...old }; if (active) next[key] = true; else delete next[key]; return next; }), []);
  const saveDefault = async (field, reason) => {
    if (busy || dirty || Object.keys(editing).length) throw new Error("Conferma i campi e attendi il ricalcolo prima di salvare.");
    try { await api.post(`/reader/jobs/${jobId}/quote-prep/review-default`, {
      decisions: { ...formRef.current, calculation_fingerprint: preview.calculation_fingerprint }, field, reason, confirm: true,
    }); await commit(field, null); }
    catch (e) { throw new Error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return { preview, form, dirty, busy, error, setError, change, recalculate, commit, onEditing, saveDefault, hasEditing: Object.keys(editing).length > 0 };
};